// src/messaging/util.js
// メッセージ画面で共通に使う小物【2026-10-16】
//   時刻の書き方・画像の縮小・localStorage の安全な読み書き・機能スイッチのフック・リアルタイム受信の共有
import { useEffect, useRef, useState } from 'react';
import { msgApi, loadFeatures } from './api';

// ── 時刻 ───────────────────────────────────────────────
const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

export function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const pad = (n) => String(n).padStart(2, '0');

/** 一覧・吹き出し用: 今日なら「14:05」、今年なら「10/3」、それより前なら「2025/10/3」 */
export function shortTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (sameDay(d, now)) return `${d.getHours()}:${pad(d.getMinutes())}`;
  if (d.getFullYear() === now.getFullYear()) return `${d.getMonth() + 1}/${d.getDate()}`;
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

export function clock(v) {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : `${d.getHours()}:${pad(d.getMinutes())}`;
}

/** 「10月16日（木）」 */
export function dayLabel(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const y = d.getFullYear() === now.getFullYear() ? '' : `${d.getFullYear()}年`;
  return `${y}${d.getMonth() + 1}月${d.getDate()}日（${WEEK[d.getDay()]}）`;
}

/** 「10月17日 9時」「10月17日 9時30分」 */
export function dateHour(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const m = d.getMinutes();
  return `${d.getMonth() + 1}月${d.getDate()}日${d.getHours()}時${m ? `${m}分` : ''}`;
}

/** 「10月16日 14:05」 */
export function dateTime(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}月${d.getDate()}日 ${d.getHours()}:${pad(d.getMinutes())}`;
}

/** 「2026年10月20日」 */
export function dateOnly(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export function fileSize(n) {
  if (n == null) return '';
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

// ── localStorage（プライベートモードなどで投げることがある）──────────
export function lsGet(key) {
  try { return window.localStorage.getItem(key); } catch (_) { return null; }
}
export function lsSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch (_) { /* 覚えられなくても動く */ }
}

// ── 添付の準備 ─────────────────────────────────────────
export const MAX_IMAGE_SIDE = 2048;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_PDF_BYTES = 10 * 1024 * 1024;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
    img.src = url;
  });
}

/**
 * 送る前の確認と縮小。
 *   PDF: 10MB を超えたら { error }
 *   画像: 長い辺が 2048px を超える（または 5MB を超える）ときは JPEG 0.85 に縮小
 * @returns {Promise<{file?: File, error?: string}>}
 */
export async function prepareAttachment(file) {
  if (!file) return {};
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    if (file.size > MAX_PDF_BYTES) return { error: 'PDF は 10MB までです' };
    return { file };
  }
  if (!file.type.startsWith('image/')) return { error: '送れるのは画像か PDF だけです' };
  let img;
  try { img = await loadImage(file); } catch (_) {
    // この端末では開けない形式（HEIC など）。そのまま送ってサーバーの判断に任せる
    return file.size > MAX_IMAGE_BYTES ? { error: '画像が大きすぎます（5MBまで）' } : { file };
  }
  const w = img.naturalWidth, h = img.naturalHeight;
  const long = Math.max(w, h);
  if (long <= MAX_IMAGE_SIDE && file.size <= MAX_IMAGE_BYTES) return { file };
  const scale = Math.min(1, MAX_IMAGE_SIDE / long);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; // 透明な部分は白にする（JPEG は透明を持てない）
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
  if (!blob) return { error: '画像を縮小できませんでした' };
  const name = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
  return { file: new File([blob], name, { type: 'image/jpeg' }) };
}

// ── 機能スイッチ ─────────────────────────────────────────
/** { features, suspended_until, loaded } */
export function useFeatures() {
  const [state, setState] = useState({ features: {}, suspended_until: null, loaded: false });
  useEffect(() => {
    let alive = true;
    loadFeatures().then(r => {
      if (alive) setState({ features: (r && r.features) || {}, suspended_until: (r && r.suspended_until) || null, loaded: true });
    });
    return () => { alive = false; };
  }, []);
  return state;
}

/** 会話の種類 → 必要な機能スイッチ（サーバーの features.featureForKind と同じ） */
export function featureForKind(kind) {
  if (kind === 'teacher_dm') return 'teacher_dm';
  if (kind === 'class_group') return 'class_chat';
  return 'student_chat';
}

export function isSuspended(until) {
  return !!until && new Date(until).getTime() > Date.now();
}

// ── リアルタイム受信（WebSocket は画面全体で1本だけ）────────────────
//   msgApi.subscribe は切れたことを知らせないので、「ready を受けたか」だけを覚えておく。
//   切れていても気づけないことがあるため、呼び出し側は遅めの取り直しも続けること。
const listeners = new Set();
let stopFn = null;
let ready = false;

function ensureSocket() {
  if (stopFn) return;
  stopFn = msgApi.subscribe((ev) => {
    if (!ev || !ev.type) return;
    if (ev.type === 'ready') ready = true;
    for (const fn of [...listeners]) { try { fn(ev); } catch (_) { /* 画面側の失敗で止めない */ } }
  });
}

export function realtimeReady() { return ready; }

/** ev => {...} を受け取る。画面から消えたら外す */
export function useRealtime(handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const fn = (ev) => ref.current && ref.current(ev);
    listeners.add(fn);
    ensureSocket();
    return () => {
      listeners.delete(fn);
      if (!listeners.size && stopFn) {
        // すぐに別の画面がつなぎ直すことが多いので、少し待ってから閉じる
        setTimeout(() => {
          if (!listeners.size && stopFn) { stopFn(); stopFn = null; ready = false; }
        }, 3000);
      }
    };
  }, []);
}

/** 窓が前に出ている（フォーカスがあり、隠れていない）か */
export function useWindowFocus() {
  const get = () => (typeof document === 'undefined' ? true
    : document.visibilityState === 'visible' && (typeof document.hasFocus !== 'function' || document.hasFocus()));
  const [focused, setFocused] = useState(get);
  useEffect(() => {
    const on = () => setFocused(get());
    window.addEventListener('focus', on);
    window.addEventListener('blur', on);
    document.addEventListener('visibilitychange', on);
    return () => {
      window.removeEventListener('focus', on);
      window.removeEventListener('blur', on);
      document.removeEventListener('visibilitychange', on);
    };
  }, []);
  return focused;
}

/** 会話の表示名（1対1・先生とのやり取りは相手の名前） */
export function conversationTitle(c, meId) {
  if (!c) return '';
  if (c.kind === 'group') return c.title || 'グループ';
  if (c.kind === 'class_group') return (c.class && c.class.name) || c.title || '授業グループ';
  let name = c.title || (c.other_user && c.other_user.name);
  if (!name && Array.isArray(c.members)) {
    const o = c.members.find(m => m.id !== meId);
    name = o && o.name;
  }
  return name || (c.kind === 'teacher_dm' ? '先生とのメッセージ' : 'メッセージ');
}

export const KIND_LABEL = {
  teacher_dm: '先生とのメッセージ',
  direct: '1対1',
  group: 'グループ',
  class_group: '授業グループ',
};
